// Geometry and unit helpers shared by the simulation, the data pipeline and the renderers.
// Local frame: metres, x = east, y = north (grid north of a stereographic projection centred on the
// aerodrome reference point). Angles: degrees, 0 = north, clockwise. Within the simulated area (< 120 NM)
// grid north differs from true north by at most ~1.5°, which is below what the game can resolve.

export const NM = 1852;
export const FT = 0.3048;
export const KT = NM / 3600; // m/s per knot
export const DEG = Math.PI / 180;
const R_EARTH = 6371008.8;

export interface LatLon { lat: number; lon: number }
export interface XY { x: number; y: number }

/** Oblique stereographic projection around a fixed origin (the conformal projection used by radar displays). */
export class Projection {
  private readonly sin0: number;
  private readonly cos0: number;
  constructor(readonly origin: LatLon) {
    this.sin0 = Math.sin(origin.lat * DEG);
    this.cos0 = Math.cos(origin.lat * DEG);
  }
  toXY(lat: number, lon: number): XY {
    const phi = lat * DEG, dl = (lon - this.origin.lon) * DEG;
    const sp = Math.sin(phi), cp = Math.cos(phi), cdl = Math.cos(dl);
    const k = (2 * R_EARTH) / (1 + this.sin0 * sp + this.cos0 * cp * cdl);
    return { x: k * cp * Math.sin(dl), y: k * (this.cos0 * sp - this.sin0 * cp * cdl) };
  }
  toLatLon(x: number, y: number): LatLon {
    const rho = Math.hypot(x, y);
    if (rho < 1e-9) return { ...this.origin };
    const c = 2 * Math.atan(rho / (2 * R_EARTH));
    const sc = Math.sin(c), cc = Math.cos(c);
    const lat = Math.asin(cc * this.sin0 + (y * sc * this.cos0) / rho);
    const lon = this.origin.lon * DEG + Math.atan2(x * sc, rho * this.cos0 * cc - y * this.sin0 * sc);
    return { lat: lat / DEG, lon: lon / DEG };
  }
}

export const dist = (ax: number, ay: number, bx: number, by: number): number => Math.hypot(bx - ax, by - ay);
export const distP = (a: XY, b: XY): number => Math.hypot(b.x - a.x, b.y - a.y);

/** Bearing from a to b in degrees [0, 360). */
export function bearing(ax: number, ay: number, bx: number, by: number): number {
  return norm360(Math.atan2(bx - ax, by - ay) / DEG);
}
export const bearingP = (a: XY, b: XY): number => bearing(a.x, a.y, b.x, b.y);

export function norm360(a: number): number {
  const r = a % 360;
  return r < 0 ? r + 360 : r;
}
/** Signed smallest difference b - a in (-180, 180]. */
export function angDiff(a: number, b: number): number {
  let d = (b - a) % 360;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}
export function polar(brgDeg: number, r: number): XY {
  return { x: Math.sin(brgDeg * DEG) * r, y: Math.cos(brgDeg * DEG) * r };
}
export function move(p: XY, brgDeg: number, r: number): XY {
  return { x: p.x + Math.sin(brgDeg * DEG) * r, y: p.y + Math.cos(brgDeg * DEG) * r };
}
export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Projection of point p onto segment ab: parameter t in [0,1], the closest point and its distance. */
export function projectOnSegment(p: XY, a: XY, b: XY): { t: number; x: number; y: number; d: number } {
  const dx = b.x - a.x, dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / l2, 0, 1) : 0;
  const x = a.x + dx * t, y = a.y + dy * t;
  return { t, x, y, d: Math.hypot(p.x - x, p.y - y) };
}

/** Cross-track distance of p from the infinite line through a with direction brg (positive = right of track). */
export function crossTrack(p: XY, a: XY, brgDeg: number): number {
  const ux = Math.sin(brgDeg * DEG), uy = Math.cos(brgDeg * DEG);
  return (p.x - a.x) * uy - (p.y - a.y) * ux;
}
/** Along-track distance of p from a along brg. */
export function alongTrack(p: XY, a: XY, brgDeg: number): number {
  const ux = Math.sin(brgDeg * DEG), uy = Math.cos(brgDeg * DEG);
  return (p.x - a.x) * ux + (p.y - a.y) * uy;
}

export function pointInPolygon(p: XY, poly: ReadonlyArray<readonly [number, number]>): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!, [xj, yj] = poly[j]!;
    if (yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** International Standard Atmosphere helpers. */
export const ISA = {
  tempC(altFt: number): number { return 15 - 1.98 * Math.min(altFt, 36089) / 1000; },
  /** True airspeed from indicated airspeed (simplified compressible-free approximation, good to ~2 %). */
  tas(iasKt: number, altFt: number, isaDev = 0): number {
    return iasKt / Math.sqrt(ISA.sigma(altFt, isaDev));
  },
  /** Density ratio rho/rho0 (troposphere; pressure from the ISA lapse rate, temperature with deviation). */
  sigma(altFt: number, isaDev = 0): number {
    const h = Math.min(altFt, 36089);
    const pRatio = Math.pow(1 - 6.8756e-6 * h, 5.2559) * (altFt > 36089 ? Math.exp(-(altFt - 36089) / 20806) : 1);
    const tRatio = (ISA.tempC(altFt) + 273.15 + isaDev) / 288.15;
    return Math.max(0.15, pRatio / tRatio);
  },
  /** Speed of sound in knots at altitude. */
  aKt(altFt: number, isaDev = 0): number { return 38.967854 * Math.sqrt(ISA.tempC(altFt) + 273.15 + isaDev); },
  /** Indicated airspeed that corresponds to a Mach number at altitude (inverse of tas()). */
  iasForMach(mach: number, altFt: number): number {
    return mach * ISA.aKt(altFt) * Math.sqrt(ISA.sigma(altFt));
  },
};
